import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { 
  Grid2X2, 
  List, 
  Search, 
  X, 
  Plus, 
  Trash, 
  Bookmark, 
  Building, 
  Map, 
  Car, 
  Utensils, 
  Eye,
  Box,
  Edit
} from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { LibraryService } from '@/lib/library-service';
import { LibraryItem } from '@/components/quotation/types';
import { formatSafeDate } from "@/lib/date-utils";
import { ItemTypeSelector, ItemType } from './ItemTypeSelector';
import { CreateItemForm } from './CreateItemForm';
import { EditLibraryItemModal } from './EditLibraryItemModal';

// Componente para renderizar os itens
function LibraryItemCard({ 
  item, 
  onSelect, 
  onDelete,
  onEdit 
}: { 
  item: LibraryItem; 
  onSelect: (item: LibraryItem) => void;
  onDelete: (itemId: string) => void;
  onEdit: (item: LibraryItem) => void;
}) {
  const renderTypeIcon = () => {
    switch (item.type) {
      case 'block':
        return <Box className="h-4 w-4 text-amber-500" />;
      case 'accommodation':
        return <Building className="h-4 w-4 text-indigo-500" />;
      case 'tour':
        return <Map className="h-4 w-4 text-emerald-500" />;
      case 'transportation':
        return <Car className="h-4 w-4 text-blue-500" />;
      case 'restaurant':
        return <Utensils className="h-4 w-4 text-red-500" />;
      default:
        return <Box className="h-4 w-4 text-gray-500" />;
    }
  };
  
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {renderTypeIcon()}
            <span className="text-xs font-medium text-gray-500 uppercase">
              {item.type}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir menu</span>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => onEdit(item)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-500 hover:text-red-600"
                onClick={() => onDelete(item.id)}
              >
                <Trash className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardTitle className="text-base">{item.title}</CardTitle>
        {item.location?.city && item.location?.country && (
          <div className="text-xs text-gray-500">
            {item.location.city}, {item.location.country}
          </div>
        )}
      </CardHeader>
      <CardContent className="pb-2 flex-grow">
        {item.description && (
          <p className="text-xs text-gray-600 line-clamp-3">{item.description}</p>
        )}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="outline" className="text-xs py-0">
                {tag}
              </Badge>
            ))}
            {item.tags.length > 3 && (
              <Badge variant="outline" className="text-xs py-0">
                +{item.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="pt-0 flex justify-between items-center">
        <span className="text-xs text-gray-500">
          Atualizado: {formatSafeDate(item.updatedAt)}
        </span>
        <Button 
          size="sm" 
          variant="outline" 
          className="h-8"
          onClick={() => onSelect(item)}
        >
          <Eye className="h-3.5 w-3.5 mr-1" />
          <span className="text-xs">Usar</span>
        </Button>
      </CardFooter>
    </Card>
  );
}

// Interface principal
interface LibraryBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectItem: (item: LibraryItem) => void;
  itemType?: string;
}

export function LibraryBrowser({
  isOpen,
  onClose,
  onSelectItem,
  itemType
}: LibraryBrowserProps) {
  // Estados
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState(itemType || 'all');
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  
  // Estados para criação e edição de itens
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTypeSelectOpen, setIsTypeSelectOpen] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState<ItemType>('block');
  
  // Estados para edição
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<LibraryItem | null>(null);
  
  // Carregar itens
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      // Buscar todos os itens
      const allItems = await LibraryService.getItems();
      
      // Filtrar itens
      let filteredItems = [...allItems];
      
      // Filtrar por tipo
      if (selectedType !== 'all') {
        filteredItems = filteredItems.filter(item => item.type === selectedType);
      }
      
      // Filtrar por país
      if (selectedCountry !== 'all') {
        filteredItems = filteredItems.filter(item => 
          item.location?.country && 
          item.location.country.toLowerCase() === selectedCountry.toLowerCase()
        );
      }
      
      // Filtrar por cidade
      if (selectedCity !== 'all') {
        filteredItems = filteredItems.filter(item => 
          item.location?.city && 
          item.location.city.toLowerCase() === selectedCity.toLowerCase()
        );
      }
      
      // Filtrar por termo de busca
      if (searchTerm) {
        const lowercaseSearch = searchTerm.toLowerCase();
        filteredItems = filteredItems.filter(item => {
          return (
            item.title.toLowerCase().includes(lowercaseSearch) ||
            (item.description && item.description.toLowerCase().includes(lowercaseSearch)) ||
            (item.tags && item.tags.some(tag => tag.toLowerCase().includes(lowercaseSearch))) ||
            (item.location?.city && item.location.city.toLowerCase().includes(lowercaseSearch)) ||
            (item.location?.country && item.location.country.toLowerCase().includes(lowercaseSearch)) ||
            (item.location?.formattedAddress && item.location.formattedAddress.toLowerCase().includes(lowercaseSearch))
          );
        });
      }
      
      // Ordenar por data de atualização (mais recente primeiro)
      filteredItems.sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      
      setItems(filteredItems);
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCountry, selectedCity, selectedType, searchTerm]);

  // Carregar países disponíveis
  const loadCountries = useCallback(async () => {
    try {
      const allItems = await LibraryService.getItems();
      const uniqueCountries = new Set<string>();
      
      allItems.forEach(item => {
        if (item.location?.country) {
          uniqueCountries.add(item.location.country);
        }
      });
      
      setCountries(Array.from(uniqueCountries).sort());
    } catch (error) {
      console.error('Erro ao carregar países:', error);
    }
  }, []);

  // Carregar cidades disponíveis baseadas no país selecionado
  const loadCities = useCallback(async () => {
    try {
      const allItems = await LibraryService.getItems();
      const uniqueCities = new Set<string>();
      
      allItems.forEach(item => {
        if (
          item.location?.city && 
          (!selectedCountry || selectedCountry === 'all' || item.location.country === selectedCountry)
        ) {
          uniqueCities.add(item.location.city);
        }
      });
      
      setCities(Array.from(uniqueCities).sort());
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
    }
  }, [selectedCountry]);
  
  // Carregar itens, países e cidades ao montar o componente
  useEffect(() => {
    if (isOpen) {
      loadItems();
      loadCountries();
    }
  }, [isOpen, loadItems, loadCountries]);
  
  // Recarregar cidades quando o país mudar
  useEffect(() => {
    if (isOpen) {
      loadCities();
    }
  }, [isOpen, selectedCountry, loadCities]);
  
  // Handler para excluir um item
  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('Tem certeza que deseja remover este item da biblioteca?')) {
      try {
        const success = await LibraryService.deleteItem(itemId);
        if (success) {
          loadItems();
        }
      } catch (error) {
        console.error('Erro ao excluir item:', error);
      }
    }
  };
  
  // Handler para iniciar o processo de edição
  const handleEditItem = (item: LibraryItem) => {
    setItemToEdit(item);
    setIsEditModalOpen(true);
  };
  
  // Handler para completar a edição
  const handleItemUpdated = (updatedItem: LibraryItem) => {
    // Recarregar a lista de itens para refletir as mudanças
    loadItems();
    
    // Fechar o modal
    setIsEditModalOpen(false);
    setItemToEdit(null);
  };
  
  // Handler para criação de novos itens
  const handleCreateNewItem = () => {
    setIsTypeSelectOpen(true);
  };
  
  // Handler para quando um item é criado
  const handleItemCreated = (item: LibraryItem) => {
    // Recarregar a lista de itens
    loadItems();
    
    // Fechar o modal
    setIsCreateModalOpen(false);
  };
  
  // Resetar filtros
  const resetFilters = () => {
    setSelectedType('all');
    setSelectedCountry('all');
    setSelectedCity('all');
    setSearchTerm('');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="h-5 w-5 text-amber-500" />
            Biblioteca de Itens
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-12 gap-4 overflow-hidden flex-grow">
          {/* Filtros (lateral) */}
          <div className="col-span-3 border-r pr-4 overflow-y-auto">
            <div className="space-y-4">
              {/* Tipo de item */}
              <div className="space-y-2">
                <Label>Tipo de Item</Label>
                <Select 
                  value={selectedType} 
                  onValueChange={setSelectedType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="block">Blocos</SelectItem>
                    <SelectItem value="restaurant">Restaurantes</SelectItem>
                    <SelectItem value="accommodation">Hospedagens</SelectItem>
                    <SelectItem value="tour">Tours</SelectItem>
                    <SelectItem value="transportation">Transportes</SelectItem>
                    <SelectItem value="flight">Voos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* País */}
              <div className="space-y-2">
                <Label>País</Label>
                <Select 
                  value={selectedCountry} 
                  onValueChange={(value) => {
                    setSelectedCountry(value);
                    setSelectedCity('all'); // Resetar cidade ao mudar país
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os países" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os países</SelectItem>
                    {countries.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Cidade */}
              <div className="space-y-2">
                <Label>Cidade</Label>
                <Select 
                  value={selectedCity} 
                  onValueChange={setSelectedCity}
                  disabled={selectedCountry === 'all'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as cidades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as cidades</SelectItem>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Reset filtros */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={resetFilters}
              >
                <X className="mr-2 h-4 w-4" />
                Limpar Filtros
              </Button>
            </div>
          </div>
          
          {/* Área de conteúdo */}
          <div className="col-span-9 flex flex-col overflow-hidden">
            {/* Barra de busca e controles */}
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-grow">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Buscar itens..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Tabs 
                defaultValue="grid" 
                value={viewMode} 
                onValueChange={(value) => setViewMode(value as 'grid' | 'list')}
                className="hidden sm:block"
              >
                <TabsList className="h-9">
                  <TabsTrigger value="grid" className="px-3">
                    <Grid2X2 className="h-4 w-4" />
                  </TabsTrigger>
                  <TabsTrigger value="list" className="px-3">
                    <List className="h-4 w-4" />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              <Button 
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={handleCreateNewItem}
              >
                <Plus className="mr-2 h-4 w-4" />
                Novo Item
              </Button>
            </div>
            
            {/* Lista de itens - Visualização em grade */}
            <div className="overflow-y-auto flex-grow">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <Bookmark className="h-12 w-12 mb-2 opacity-20" />
                  <p>Nenhum item encontrado.</p>
                  <p className="text-sm">Tente outros filtros ou adicione novos itens.</p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item) => (
                    <LibraryItemCard
                      key={item.id}
                      item={item}
                      onSelect={onSelectItem}
                      onDelete={handleDeleteItem}
                      onEdit={handleEditItem}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((item) => (
                    <Card key={item.id} className="overflow-hidden">
                      <div className="flex items-center p-3">
                        <div className="flex-shrink-0 mr-3">
                          {item.type === 'block' && <Box className="h-5 w-5 text-amber-500" />}
                          {item.type === 'accommodation' && <Building className="h-5 w-5 text-indigo-500" />}
                          {item.type === 'tour' && <Map className="h-5 w-5 text-emerald-500" />}
                          {item.type === 'transportation' && <Car className="h-5 w-5 text-blue-500" />}
                          {item.type === 'restaurant' && <Utensils className="h-5 w-5 text-red-500" />}
                        </div>
                        <div className="flex-grow">
                          <h3 className="font-medium text-sm">{item.title}</h3>
                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                            <span className="uppercase mr-2">{item.type}</span>
                            {item.location?.city && (
                              <span>{item.location.city}, {item.location.country}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => onSelectItem(item)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            <span className="text-xs">Usar</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-blue-500 hover:text-blue-600"
                            onClick={() => handleEditItem(item)}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteItem(item.id)}
                          >
                            <Trash className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      
      {/* Modal para seleção de tipo de item */}
      {isTypeSelectOpen && (
        <ItemTypeSelector
          isOpen={isTypeSelectOpen}
          onClose={() => setIsTypeSelectOpen(false)}
          onSelectType={(type: string) => {
            setSelectedItemType(type as ItemType);
            setIsTypeSelectOpen(false);
            setIsCreateModalOpen(true);
          }}
        />
      )}
      
      {/* Modal para criação de item */}
      {isCreateModalOpen && (
        <CreateItemForm
          isOpen={isCreateModalOpen}
          itemType={selectedItemType as any}
          onClose={() => setIsCreateModalOpen(false)}
          onItemCreated={handleItemCreated}
        />
      )}
      
      {/* Modal para edição de item */}
      {isEditModalOpen && itemToEdit && (
        <EditLibraryItemModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setItemToEdit(null);
          }}
          libraryItem={itemToEdit}
          onItemUpdated={handleItemUpdated}
        />
      )}
    </Dialog>
  );
} 